import { type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type CardVariant = 'solid' | 'glass' | 'outline';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  /** Lift and glow on hover. Only use on cards that are actually interactive. */
  interactive?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const VARIANT_CLASSES: Record<CardVariant, string> = {
  solid: 'surface-card shadow-[var(--shadow-lifted)]',
  glass: 'glass',
  outline: 'border border-[var(--border-subtle)] bg-transparent',
};

const PADDING_CLASSES = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
} as const;

export function Card({
  variant = 'solid',
  interactive = false,
  padding = 'md',
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={cn(
        'rounded-2xl transition-all duration-300 ease-[var(--ease-smooth)]',
        VARIANT_CLASSES[variant],
        PADDING_CLASSES[padding],
        interactive && 'hover:-translate-y-1 hover:shadow-[var(--shadow-glow)] cursor-pointer',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('mb-4 flex flex-col gap-1.5', className)} {...rest}>
      {children}
    </div>
  );
}

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Heading level. Choose the one that fits the page outline, not the size. */
  as?: 'h2' | 'h3' | 'h4';
  children: ReactNode;
}

export function CardTitle({ as: Tag = 'h3', className, children, ...rest }: CardTitleProps) {
  return (
    <Tag className={cn('text-lg font-semibold tracking-tight', className)} {...rest}>
      {children}
    </Tag>
  );
}

export function CardDescription({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn('text-sm text-[var(--text-secondary)]', className)} {...rest}>
      {children}
    </p>
  );
}

export function CardContent({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('text-sm text-[var(--text-secondary)]', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('mt-5 flex items-center gap-3', className)} {...rest}>
      {children}
    </div>
  );
}
